import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { api } from '../utils/api';

const AudioPlayerContext = createContext(null);

export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error('useAudioPlayer must be used within AudioPlayerProvider');
  }
  return context;
};

export const AudioPlayerProvider = ({ children }) => {
  const [currentStation, setCurrentStation] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.crossOrigin = 'anonymous';
    }

    const audio = audioRef.current;

    const handlePlaying = () => {
      setLoading(false);
      setIsPlaying(true);
      setError(null);
    };
    const handlePause = () => setIsPlaying(false);
    const handleWaiting = () => setLoading(true);
    const handleError = () => {
      setLoading(false);
      setIsPlaying(false);
      setError('Stream unavailable. This station may be offline or the stream URL is invalid.');
    };
    const handleCanPlay = () => setLoading(false);

    audio.addEventListener('playing', handlePlaying);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('error', handleError);
    audio.addEventListener('canplay', handleCanPlay);

    return () => {
      audio.removeEventListener('playing', handlePlaying);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplay', handleCanPlay);
    };
  }, []);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  const playStation = (station) => {
    if (!audioRef.current) return;

    // If it's the same station, toggle play/pause
    if (currentStation?.station_id === station.station_id) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(() => {
          setError('Unable to play this stream.');
          setLoading(false);
        });
      }
      return;
    }

    // New station - stop current, load new
    audioRef.current.pause();
    audioRef.current.src = station.stream_url;
    setCurrentStation(station);
    setLoading(true);
    setError(null);
    
    // Track click for Radio Browser popularity stats
    api.trackStationClick(station.station_id).catch(() => {});
    
    audioRef.current.play().catch(() => {
      setError('Unable to play this stream.');
      setLoading(false);
      setIsPlaying(false);
    });
  };

  const togglePlay = () => {
    if (!audioRef.current || !currentStation) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(() => {
        setError('Unable to play this stream.');
      });
    }
  };

  const stopPlayer = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    setCurrentStation(null);
    setIsPlaying(false);
    setLoading(false);
    setError(null);
  };

  return (
    <AudioPlayerContext.Provider
      value={{
        currentStation,
        isPlaying,
        volume,
        loading,
        error,
        playStation,
        togglePlay,
        stopPlayer,
        setVolume,
      }}
    >
      {children}
    </AudioPlayerContext.Provider>
  );
};
